"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Calendar, User } from "lucide-react";

function MainPostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse">
        <div className="mb-8 h-12 w-3/4 rounded-lg bg-muted"></div>
        <div className="mb-12 flex gap-4">
          <div className="h-6 w-32 rounded bg-muted"></div>
          <div className="h-6 w-32 rounded bg-muted"></div>
        </div>
        <div className="mb-12 h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-foreground">Article not found</h2>
        <p className="mt-4 text-muted-foreground">The pet care guide you are looking for is currently unavailable.</p>
        <Link to="/HomePage" className="mt-8 inline-block">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      <header className="mb-12 text-center">
        <h1 className="font-default mb-6 text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["48"] || ""} alt={post.author.name} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  <User className="size-4" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-16 overflow-hidden rounded-lg shadow-xl shadow-primary/5">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto max-h-[600px] w-full object-cover"
            loading="eager"
          />
        </div>
      )}

      <div
        className="prose prose-lg mx-auto max-w-none font-default text-foreground/90 marker:text-primary prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-a:text-primary hover:prose-a:text-primary/80 prose-img:rounded-lg"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {post.author && (
        <div className="mt-24 flex flex-col items-center gap-6 rounded-lg border border-border bg-card p-8 text-center md:flex-row md:items-start md:text-left shadow-md">
          <Avatar className="size-24 border-2 border-primary/20">
            <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
            <AvatarFallback className="bg-primary/10 text-2xl text-primary">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="font-default mb-2 text-xl font-bold text-foreground">
              Written by {post.author.name}
            </h3>
            <p className="text-muted-foreground font-default">
              Veterinary specialist at Happy Paws Animal Hospital. Passionate about preventative care, surgical excellence, and helping your furry family members live their healthiest, happiest lives.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-[400px] animate-pulse rounded-lg bg-muted"></div>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card
          key={post.id}
          data-index={i}
          className="group flex h-full flex-col overflow-hidden border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          {post.featuredImage && (
            <div className="relative h-48 overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>
          )}
          <CardHeader className="flex-none">
            <CardTitle className="font-default line-clamp-2 text-xl text-foreground group-hover:text-primary transition-colors">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1">
            <div
              className="line-clamp-3 text-sm text-muted-foreground font-default"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="flex-none pt-4">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between px-0 text-primary hover:bg-transparent hover:text-primary/80 font-semibold">
                Read More
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const mainQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section data-section-id="244775"
      id="single-post-template-section"
      className="bg-background text-foreground py-24 px-4 md:px-8 lg:py-32"
    >
      <div className="mx-auto max-w-screen-xl">
        <PostsProvider wp_query={mainQuery}>
          <MainPostContent />
        </PostsProvider>

        <div className="mx-auto mt-24 max-w-4xl">
          <Separator className="bg-border" />
        </div>

        <div className="mt-24">
          <div className="mb-12 flex items-center justify-between">
            <h2 className="font-default text-3xl font-bold text-foreground md:text-4xl">
              More Pet Care Tips
            </h2>
            <Link to="/ServicesPage">
              <Button variant="outline" className="hidden md:flex border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
                View All Articles
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsContent />
          </PostsProvider>
          
          <div className="mt-8 flex justify-center md:hidden">
            <Link to="/ServicesPage" className="w-full">
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
                View All Articles
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}